/** Error codes emitted by the new parrot-subscriptions-api */
export type ApiErrorCode =
  | 'BAD_REQUEST'
  | 'VALIDATION_ERROR'
  | 'UNAUTHORIZED'
  | 'NOT_FOUND'
  | 'CONFLICT'
  | 'INTERNAL_ERROR';

/**
 * Normalized error thrown by the new subscriptions axios client.
 * Extends native Error so React Query and async stacks treat it as one.
 */
export type ApiError = Error & {
  readonly name: 'ApiError';
  readonly code: ApiErrorCode | string;
  readonly status: number;
  readonly details?: unknown;
};

/**
 * Creates a normalized ApiError from a parsed API error body.
 *
 * @param params - Fields extracted from the `{success:false, error:{...}}` body plus HTTP status
 * @returns An ApiError instance (also an Error at runtime)
 */
export const createApiError = (params: {
  code: string;
  message: string;
  status: number;
  details?: unknown;
}): ApiError => {
  const err = new Error(params.message) as ApiError;
  return Object.assign(err, {
    name: 'ApiError' as const,
    code: params.code,
    status: params.status,
    details: params.details,
  });
};

/**
 * Type guard that narrows an unknown value to ApiError.
 * Prefer this over `instanceof` since ApiError is a branded Error, not a class.
 */
export const isApiError = (value: unknown): value is ApiError =>
  value instanceof Error && (value as ApiError).name === 'ApiError';
